import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';

import IndeedAutoFillLogo from './assets/autofill-indeed-logo.svg';
import LinkedInAutoFillDivider from './assets/autofill-linkedin-divider.svg';
import LinkedInAutoFillLogo from './assets/autofill-linkedin-logo.svg';
import SeekAutoFillLogo from './assets/autofill-seek-logo.svg';
import { useIndeedOverlayPortal } from './hooks/useIndeedOverlayPortal';
import { useLinkedInImport } from './hooks/useLinkedInImport';
import { useResumeParser } from './hooks/useResumeParser';
import { classes } from './styles';
import {
  fillCountryThenState,
  fillParsedFields,
  findFileExtension,
  hasValidValue
} from './utils/autofillFields';
import DocTypeIcon from '../NewUI/Components/Utilities/DocTypeIcon';
import { humanFileSize } from '../NewUI/Components/Utilities/humanFileSize';

import type {
  AutoFillConfig,
  AutoFillProvider,
  AutoFillProviderKind,
  AutoFillResumeFile,
  AutoFillText,
  ParseResumeErrorCode,
  ParseResumeResponseData,
  PendingLocation
} from './types';

interface AutoFillPersonalDetailsProps {
  config?: AutoFillConfig;
  countriesReady?: boolean;
  fontFamily?: string;
  isParsing?: boolean;
  parseError?: string;
  selectedResumeFile?: AutoFillResumeFile;
  onSelectedResumeFileChange?: (file: AutoFillResumeFile) => void;
  onParsingProgressChange?: (progress: number, visible: boolean) => void;
  onParsingStateChange?: (parsing: boolean, error?: string) => void;
}

interface AutoFillUploadCardContentProps {
  selectedFile: AutoFillResumeFile;
  isDragActive: boolean;
  isParsing: boolean;
  uploadError: string;
  fontFamily?: string;
  text?: AutoFillText;
  onClearSelectedFile: () => void;
  onOpenFilePicker: () => void;
}

interface IndeedProviderShellProps {
  provider: AutoFillProvider;
  providerCount: number;
  isParsing: boolean;
  overlaySlotRef: React.RefObject<HTMLDivElement | null>;
  onClickCapture: () => void;
}

const ACCEPTED_PROVIDER_ORDER: AutoFillProviderKind[] = ['seek', 'indeed', 'linkedin'];
const DEFAULT_ACCEPTED_EXTENSIONS = ['pdf', 'docx', 'doc'];
const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const AutoFillProviderContent = ({ provider }: { provider: AutoFillProvider }) => {
  if (provider.kind === 'seek') {
    return (
      <>
        <Box
          aria-hidden="true"
          className="wcag-autofill-provider-label"
          component="span"
          sx={classes.autoFillPersonalDetails.providerLabelSx()}
        >
          {provider.prefix ?? provider.label}
        </Box>
        <Box
          aria-hidden="true"
          className="wcag-autofill-provider-logo"
          component="span"
          sx={classes.autoFillPersonalDetails.providerLogoSx('seek')}
        >
          <Box alt="" component="img" src={SeekAutoFillLogo} />
        </Box>
      </>
    );
  }

  if (provider.kind === 'linkedin') {
    return (
      <>
        <Box
          className="wcag-autofill-provider-brand-group"
          component="span"
          sx={classes.autoFillPersonalDetails.providerBrandGroupSx()}
        >
          <Box
            aria-hidden="true"
            className="wcag-autofill-provider-logo"
            component="span"
            sx={classes.autoFillPersonalDetails.providerLogoSx('linkedin')}
          >
            <Box alt="" component="img" src={LinkedInAutoFillLogo} />
          </Box>
          <Box
            aria-hidden="true"
            className="wcag-autofill-provider-divider"
            component="span"
            sx={classes.autoFillPersonalDetails.providerDividerSx()}
          >
            <Box alt="" component="img" src={LinkedInAutoFillDivider} />
          </Box>
        </Box>
        <Box
          aria-hidden="true"
          className="wcag-autofill-provider-label"
          component="span"
          sx={classes.autoFillPersonalDetails.providerLabelSx()}
        >
          {provider.label}
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        aria-hidden="true"
        className="wcag-autofill-provider-logo"
        component="span"
        sx={classes.autoFillPersonalDetails.providerLogoSx('indeed')}
      >
        <Box alt="" component="img" src={IndeedAutoFillLogo} />
      </Box>
      <Box
        className="wcag-autofill-provider-label"
        component="span"
        sx={classes.autoFillPersonalDetails.providerLabelSx()}
      >
        {provider.label}
      </Box>
    </>
  );
};

const IndeedProviderShell = ({
  provider,
  providerCount,
  isParsing,
  overlaySlotRef,
  onClickCapture
}: IndeedProviderShellProps) => (
  <Box
    className={`wcag-autofill-provider-button wcag-autofill-provider-button--${provider.kind}`}
    data-disabled={isParsing ? 'true' : undefined}
    onClickCapture={onClickCapture}
    sx={classes.autoFillPersonalDetails.indeedShellSx(providerCount)}
  >
    <Box
      aria-hidden="true"
      className="wcag-autofill-provider-shell-content"
      component="span"
      sx={classes.autoFillPersonalDetails.indeedShellContentSx()}
    >
      <AutoFillProviderContent provider={provider} />
    </Box>

    <Box
      ref={overlaySlotRef}
      className="wcag-autofill-indeed-overlay-slot"
      sx={classes.autoFillPersonalDetails.indeedOverlaySlotSx()}
    />

    {isParsing ? (
      <Box
        aria-hidden="true"
        className="wcag-autofill-indeed-overlay-blocker"
        sx={classes.autoFillPersonalDetails.indeedOverlayBlockerSx()}
      />
    ) : null}
  </Box>
);

const AutoFillUploadCardContent = ({
  selectedFile,
  isDragActive,
  isParsing,
  uploadError,
  fontFamily,
  text,
  onClearSelectedFile,
  onOpenFilePicker
}: AutoFillUploadCardContentProps) => {
  if (selectedFile) {
    return (
      <Box sx={classes.autoFillPersonalDetails.selectedFileContainerSx()}>
        <Box
          className="wcag-autofill-selected-file-button"
          component="button"
          data-testid="wcag-autofill-selected-file-button"
          disabled={isParsing}
          onClick={onClearSelectedFile}
          sx={classes.autoFillPersonalDetails.selectedFileButtonSx(!!uploadError, isParsing)}
          type="button"
        >
          <Box
            aria-hidden="true"
            className="wcag-autofill-selected-file-icon-tile"
            sx={classes.autoFillPersonalDetails.selectedFileIconTileSx()}
          >
            <Box
              className="wcag-autofill-selected-file-doc-icon"
              sx={classes.autoFillPersonalDetails.selectedFileIconLayerSx()}
            >
              <Box sx={classes.autoFillPersonalDetails.selectedFileDocTypeIconSx()}>
                {DocTypeIcon(findFileExtension(selectedFile.name))}
              </Box>
            </Box>
            <Box
              className="wcag-autofill-selected-file-action-icon"
              sx={classes.autoFillPersonalDetails.selectedFileIconLayerSx()}
            >
              <ClearIcon sx={classes.autoFillPersonalDetails.selectedFileClearIconSx()} />
            </Box>
          </Box>

          <Box sx={classes.autoFillPersonalDetails.selectedFileTextGroupSx()}>
            <Box sx={classes.autoFillPersonalDetails.selectedFilePrimaryTextSx()}>
              <Typography
                className="wcag-autofill-selected-file-filename"
                component="span"
                sx={classes.autoFillPersonalDetails.selectedFileNameSx(fontFamily)}
              >
                {selectedFile.name}
              </Typography>
              <Typography
                className="wcag-autofill-selected-file-action-label"
                component="span"
                sx={classes.autoFillPersonalDetails.selectedFileActionLabelSx(fontFamily)}
              >
                {text?.selectedFileAction}
              </Typography>
            </Box>

            <Typography
              className="wcag-autofill-selected-file-meta"
              component="span"
              sx={classes.autoFillPersonalDetails.selectedFileMetaSx(fontFamily)}
            >
              {humanFileSize(selectedFile.size)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isDragActive) {
    return (
      <Box className="wcag-autofill-upload-row" sx={classes.autoFillPersonalDetails.uploadRowSx()}>
        <UploadIcon
          className="wcag-autofill-upload-icon"
          sx={classes.autoFillPersonalDetails.uploadIconSx()}
        />
        <Typography
          className="wcag-autofill-upload-prompt"
          component="span"
          sx={classes.autoFillPersonalDetails.uploadPromptSx(fontFamily)}
        >
          {text?.dropPrompt}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box className="wcag-autofill-upload-row" sx={classes.autoFillPersonalDetails.uploadRowSx()}>
        <UploadIcon
          className="wcag-autofill-upload-icon"
          sx={classes.autoFillPersonalDetails.uploadIconSx()}
        />
        <Typography
          className="wcag-autofill-upload-prompt"
          component="span"
          sx={classes.autoFillPersonalDetails.uploadPromptSx(fontFamily)}
        >
          {text?.dragPrompt}
        </Typography>
        <Button
          className="wcag-autofill-choose-button"
          disabled={isParsing}
          onClick={onOpenFilePicker}
          sx={classes.autoFillPersonalDetails.chooseButtonSx(!!uploadError)}
          type="button"
        >
          {text?.chooseResume}
        </Button>
      </Box>

      <Box>
        <Typography
          className="wcag-autofill-helper-text"
          component="div"
          sx={classes.autoFillPersonalDetails.helperTextSx(fontFamily)}
        >
          {text?.helperSize}
        </Typography>
        <Typography
          className="wcag-autofill-helper-text"
          component="div"
          sx={classes.autoFillPersonalDetails.helperTextSx(fontFamily, '0 !important')}
        >
          {text?.helperFormats}
        </Typography>
      </Box>
    </>
  );
};

const AutoFillPersonalDetails = ({
  config,
  countriesReady = false,
  fontFamily,
  isParsing = false,
  parseError = '',
  selectedResumeFile,
  onSelectedResumeFileChange,
  onParsingProgressChange,
  onParsingStateChange
}: AutoFillPersonalDetailsProps) => {
  const providers = useMemo(
    () =>
      [...(config?.providers ?? [])].sort(
        (left, right) =>
          ACCEPTED_PROVIDER_ORDER.indexOf(left.kind) - ACCEPTED_PROVIDER_ORDER.indexOf(right.kind)
      ),
    [config?.providers]
  );
  const providerCount = providers.length;
  const acceptedExtensions = useMemo(
    () =>
      (config?.acceptedExtensions?.length
        ? config.acceptedExtensions
        : DEFAULT_ACCEPTED_EXTENSIONS
      ).map((extension) => extension.toLowerCase()),
    [config?.acceptedExtensions]
  );
  const acceptAttribute = useMemo(
    () => acceptedExtensions.map((extension) => `.${extension}`).join(','),
    [acceptedExtensions]
  );
  const maxFileSizeBytes = config?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
  const linkedInProvider = providers.find((provider) => provider.kind === 'linkedin');
  const hasIndeedProvider = providers.some((provider) => provider.kind === 'indeed');
  const parseEndpoint = config?.parseEndpoint;
  const text = config?.text;
  const selectedFile = selectedResumeFile ?? null;
  const indeedOverlaySlotRef = useRef<HTMLDivElement | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [pendingLocation, setPendingLocation] = useState<PendingLocation | null>(null);
  const activeError = uploadError !== '' ? uploadError : parseError;

  const handleParsedFields = useCallback(
    (data: ParseResumeResponseData) => {
      fillParsedFields(data, { countriesReady, setPendingLocation });
    },
    [countriesReady]
  );

  const { cancelActiveParse, clearParseFeedback, resolveErrorMessage, triggerParse } =
    useResumeParser({
      parseEndpoint,
      text,
      onParsed: handleParsedFields,
      onParsingProgressChange,
      onParsingStateChange
    });

  useIndeedOverlayPortal({
    overlaySlotRef: indeedOverlaySlotRef,
    hasIndeedProvider,
    isParsing
  });

  const openLinkedInImport = useLinkedInImport({
    providerUrl: linkedInProvider?.url
  });

  useEffect(() => {
    const jobRoot = document.getElementById('job');
    jobRoot?.classList.add('wcag-autofill-ready');

    return () => {
      jobRoot?.classList.remove('wcag-autofill-ready');
    };
  }, []);

  useEffect(() => {
    if (!countriesReady || !pendingLocation || !hasValidValue(pendingLocation.countryId)) {
      return;
    }

    fillCountryThenState(pendingLocation.countryId, pendingLocation.stateId);
    setPendingLocation(null);
  }, [countriesReady, pendingLocation]);

  const validateFile = useCallback(
    (file: File): ParseResumeErrorCode | null => {
      const extension = findFileExtension(file.name);

      if (!acceptedExtensions.includes(extension)) {
        return 'unsupported_file_format';
      }

      if (maxFileSizeBytes > 0 && file.size > maxFileSizeBytes) {
        return 'file_too_large';
      }

      return null;
    },
    [acceptedExtensions, maxFileSizeBytes]
  );

  const handleFileSelection = useCallback(
    (file: File | null) => {
      if (!file) {
        return;
      }

      const nextErrorCode = validateFile(file);
      if (nextErrorCode) {
        setUploadError(resolveErrorMessage(nextErrorCode));
        cancelActiveParse();
        clearParseFeedback();
        return;
      }

      setUploadError('');
      clearParseFeedback();
      onSelectedResumeFileChange?.(file);
      triggerParse(file);
    },
    [
      cancelActiveParse,
      clearParseFeedback,
      onSelectedResumeFileChange,
      resolveErrorMessage,
      triggerParse,
      validateFile
    ]
  );

  const { getInputProps, getRootProps, inputRef, isDragActive, open } = useDropzone({
    disabled: isParsing,
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    multiple: false,
    onDrop: (acceptedFiles) => {
      handleFileSelection(acceptedFiles[0] ?? null);
    }
  });

  useEffect(() => {
    if (uploadError && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [uploadError, inputRef]);

  const handleClearSelectedFile = useCallback(() => {
    cancelActiveParse();
    clearParseFeedback();
    onSelectedResumeFileChange?.(null);
    setUploadError('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [cancelActiveParse, clearParseFeedback, inputRef, onSelectedResumeFileChange]);

  const handleProviderClick = useCallback(
    (provider: AutoFillProvider) => {
      clearParseFeedback();

      if (provider.disabled) {
        return;
      }

      if (provider.kind === 'seek' && provider.url) {
        window.location.assign(provider.url);
        return;
      }

      if (provider.kind === 'linkedin') {
        openLinkedInImport();
      }
    },
    [clearParseFeedback, openLinkedInImport]
  );

  return (
    <Box
      aria-labelledby="autofill-personal-details-heading"
      sx={classes.autoFillPersonalDetails.containerSx()}
    >
      <Typography
        component="h3"
        id="autofill-personal-details-heading"
        sx={classes.autoFillPersonalDetails.headingSx(fontFamily)}
      >
        {text?.heading}
      </Typography>

      <Typography sx={classes.autoFillPersonalDetails.descriptionSx(providerCount, fontFamily)}>
        {text?.description}
      </Typography>

      <Box
        className="wcag-autofill-content-row"
        sx={classes.autoFillPersonalDetails.contentRowSx(providerCount)}
      >
        <Box
          {...getRootProps({
            className: 'wcag-autofill-upload-card',
            'data-testid': 'wcag-autofill-upload-card'
          })}
          sx={classes.autoFillPersonalDetails.uploadCardSx(
            providerCount,
            isDragActive,
            !!uploadError,
            isParsing
          )}
        >
          <input
            {...getInputProps({
              accept: acceptAttribute,
              'data-testid': 'wcag-autofill-file-input',
              style: classes.shared.validatableHiddenInputStyle
            })}
          />

          <Box sx={classes.autoFillPersonalDetails.uploadCardInnerSx(!!selectedFile)}>
            <AutoFillUploadCardContent
              fontFamily={fontFamily}
              isDragActive={isDragActive}
              isParsing={isParsing}
              onClearSelectedFile={handleClearSelectedFile}
              onOpenFilePicker={open}
              selectedFile={selectedFile}
              text={text}
              uploadError={uploadError}
            />
          </Box>
        </Box>

        {providerCount > 0 ? (
          <Typography sx={classes.autoFillPersonalDetails.orTextSx()}>{text?.separator}</Typography>
        ) : null}

        {providerCount > 0 ? (
          <Box
            className="wcag-autofill-providers-grid"
            sx={classes.autoFillPersonalDetails.providersGridSx(providerCount)}
          >
            {providers.map((provider) =>
              provider.kind === 'indeed' ? (
                <IndeedProviderShell
                  key={provider.kind}
                  isParsing={isParsing}
                  onClickCapture={clearParseFeedback}
                  overlaySlotRef={indeedOverlaySlotRef}
                  provider={provider}
                  providerCount={providerCount}
                />
              ) : (
                <Button
                  key={provider.kind}
                  aria-label={provider.label}
                  className={`wcag-autofill-provider-button wcag-autofill-provider-button--${provider.kind}`}
                  disabled={isParsing || provider.disabled}
                  onClick={() => handleProviderClick(provider)}
                  type="button"
                  sx={classes.autoFillPersonalDetails.providerButtonSx(
                    provider.kind,
                    providerCount
                  )}
                >
                  <AutoFillProviderContent provider={provider} />
                </Button>
              )
            )}
          </Box>
        ) : null}
      </Box>

      {activeError ? (
        <Box data-validation-error role="alert" sx={classes.autoFillPersonalDetails.errorSx()}>
          {activeError}
        </Box>
      ) : null}
    </Box>
  );
};

export default AutoFillPersonalDetails;
